import bbox from "@turf/bbox";
import centroid from "@turf/centroid";
import intersect from "@turf/intersect";
import voronoi from "@turf/voronoi";

import { featureCollection, point } from "@turf/helpers";

import proj4 from "proj4";

import geoFileConfig from "./geoFile.config";

/* =========================================================
   Constants
========================================================= */

const SUPPORTED_TRACE_TYPES = new Set([
  "bar",
  "scatter",
  "line",
  "pie",
  "heatmap",
  "scatterpolar",
  "barpolar",
]);

const TURBO_COLOR_SCALE = [
  [0.0, "#30123b"],
  [0.08, "#4145ab"],
  [0.16, "#4675ed"],
  [0.25, "#39a2fc"],
  [0.33, "#1bcfd4"],
  [0.42, "#24eca6"],
  [0.5, "#61fc6c"],
  [0.58, "#a4fc3b"],
  [0.67, "#d8e219"],
  [0.75, "#f9ba13"],
  [0.83, "#f7810a"],
  [0.92, "#e5470b"],
  [1.0, "#a80d0d"],
];

/* =========================================================
   Plotly UI configuration
========================================================= */

function buildDownloadOnlyPlotlyConfig(baseConfig = {}, filename = "plot") {
  return {
    ...(baseConfig || {}),

    /* Show only one UI action: download the current chart as an image. */
    displayModeBar: "hover",
    displaylogo: false,
    responsive: true,

    /* Replace Plotly's default toolbar with only the image button. */
    modeBarButtons: [["toImage"]],

    toImageButtonOptions: {
      format: "png",
      filename: String(filename || "plot"),
      scale: 2,
      ...(baseConfig?.toImageButtonOptions || {}),
    },
  };
}

/* =========================================================
   GeoJSON configuration access
========================================================= */

/**
 * Retrieves a GeoJSON object from the user's geoFile.config.js.
 *
 * The lookup is exact:
 *
 * boundary_file: "تهران.json"
 *
 * resolves only:
 *
 * geoFileConfig["تهران.json"]
 *
 * No filename normalization, character conversion,
 * prefix insertion, URL encoding or extension modification
 * is performed.
 */
function getGeoJsonByBoundaryFile(boundaryFile) {
  if (typeof boundaryFile !== "string" || boundaryFile.length === 0) {
    throw new Error("map.boundary_file is missing.");
  }

  const geoJson = geoFileConfig[boundaryFile];

  if (!geoJson) {
    const registeredFiles = Object.keys(geoFileConfig);

    throw new Error(
      `GeoJSON "${boundaryFile}" is not registered in geoFile.config.js. ` +
        `Registered files: ${registeredFiles.join(", ")}`,
    );
  }

  if (geoJson.type === "FeatureCollection" && Array.isArray(geoJson.features)) {
    return geoJson;
  }

  if (geoJson.type === "Feature" && geoJson.geometry) {
    return featureCollection([geoJson]);
  }

  throw new Error(
    `The value registered for "${boundaryFile}" is not a valid ` +
      "GeoJSON Feature or FeatureCollection.",
  );
}

export function getRegisteredGeoJsonFiles() {
  return Object.keys(geoFileConfig);
}

export function hasRegisteredGeoJson(boundaryFile) {
  return Object.prototype.hasOwnProperty.call(geoFileConfig, boundaryFile);
}

/* =========================================================
   General utilities
========================================================= */

function sanitizePlotlyValue(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (Array.isArray(value)) {
    return value.map(sanitizePlotlyValue);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        sanitizePlotlyValue(nestedValue),
      ]),
    );
  }

  return value;
}

function toFiniteNumber(value, fieldName) {
  const convertedValue = Number(value);

  if (!Number.isFinite(convertedValue)) {
    throw new Error(
      `Invalid numeric value for "${fieldName}": ${String(value)}`,
    );
  }

  return convertedValue;
}

function toNullableNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const convertedValue = Number(value);

  return Number.isFinite(convertedValue) ? convertedValue : null;
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };

    return entities[character];
  });
}

/*
 * Plotly does not expose a CSS-like `direction: rtl` option for SVG
 * title / legend text. Unicode bidi embedding keeps Persian/Arabic
 * labels right-to-left without changing the chart layout.
 */
const RTL_EMBED_START = "\u202B";
const RTL_EMBED_END = "\u202C";

function makeRtlText(value) {
  if (typeof value !== "string" || value.length === 0) {
    return value;
  }

  if (value.startsWith(RTL_EMBED_START) && value.endsWith(RTL_EMBED_END)) {
    return value;
  }

  return RTL_EMBED_START + value + RTL_EMBED_END;
}

function applyRtlTitleAndLegends(data, layout) {
  const rtlData = (data || []).map((trace) => {
    if (trace && typeof trace.name === "string") {
      return {
        ...trace,
        name: makeRtlText(trace.name),
      };
    }

    return trace;
  });

  const rtlLayout = {
    ...(layout || {}),
  };

  if (typeof rtlLayout.title === "string") {
    rtlLayout.title = makeRtlText(rtlLayout.title);
  } else if (
    rtlLayout.title &&
    typeof rtlLayout.title === "object" &&
    typeof rtlLayout.title.text === "string"
  ) {
    rtlLayout.title = {
      ...rtlLayout.title,
      text: makeRtlText(rtlLayout.title.text),
    };
  }

  if (
    rtlLayout.legend?.title &&
    typeof rtlLayout.legend.title === "object" &&
    typeof rtlLayout.legend.title.text === "string"
  ) {
    rtlLayout.legend = {
      ...rtlLayout.legend,
      title: {
        ...rtlLayout.legend.title,
        text: makeRtlText(rtlLayout.legend.title.text),
      },
    };
  }

  return {
    data: rtlData,
    layout: rtlLayout,
  };
}

function resolveColorScale(colorScale) {
  if (typeof colorScale === "string" && colorScale.toLowerCase() === "turbo") {
    return TURBO_COLOR_SCALE;
  }

  return colorScale || TURBO_COLOR_SCALE;
}

function calculateColorRange(values) {
  const finiteValues = values.filter(
    (value) => typeof value === "number" && Number.isFinite(value),
  );

  if (finiteValues.length === 0) {
    throw new Error("The map does not contain any valid numeric values.");
  }

  let minimum = Math.min(...finiteValues);

  let maximum = Math.max(...finiteValues);

  /*
   * Plotly needs a non-zero color range.
   */
  if (minimum === maximum) {
    const offset = Math.abs(minimum) * 0.01 || 1;

    minimum -= offset;
    maximum += offset;
  }

  return {
    minimum,
    maximum,
  };
}

/**
 * Resolve the numeric domain used by Plotly for map coloring.
 *
 * Semantic map specs (AQI and pollutant concentration) already carry an
 * authoritative `encoding.range_color` from the backend. That range MUST win
 * over the values currently present in the result set; otherwise Plotly
 * stretches the stepped semantic colorscale over the observed min/max and the
 * health-category boundaries move from one query to another.
 *
 * Non-semantic maps do not provide `range_color`, so they keep the previous
 * behavior and derive their color domain from the observed data.
 */
function resolveMapColorRange(values, configuredRange) {
  if (Array.isArray(configuredRange) && configuredRange.length === 2) {
    const configuredMinimum = Number(configuredRange[0]);
    const configuredMaximum = Number(configuredRange[1]);

    if (
      Number.isFinite(configuredMinimum) &&
      Number.isFinite(configuredMaximum) &&
      configuredMaximum > configuredMinimum
    ) {
      return {
        minimum: configuredMinimum,
        maximum: configuredMaximum,
      };
    }

    console.warn(
      "Ignoring invalid map encoding.range_color; falling back to the observed data range:",
      configuredRange,
    );
  }

  return calculateColorRange(values);
}

/**
 * Only used to match province names inside GeoJSON.
 *
 * This function is never applied to filenames.
 */
function normalizeProvinceName(value) {
  return String(value ?? "")
    .normalize("NFKC")
    .replaceAll("ي", "ی")
    .replaceAll("ى", "ی")
    .replaceAll("ك", "ک")
    .replace(/\s+/g, " ")
    .trim();
}

/* =========================================================
   GeoJSON geometry utilities
========================================================= */

function isAreaFeature(feature) {
  return (
    feature?.geometry?.type === "Polygon" ||
    feature?.geometry?.type === "MultiPolygon"
  );
}

function extractAreaFeatures(geoJson) {
  const areaFeatures = geoJson.features.filter(isAreaFeature);

  if (areaFeatures.length === 0) {
    throw new Error(
      "The selected GeoJSON does not contain Polygon or MultiPolygon features.",
    );
  }

  return areaFeatures;
}

function transformCoordinates(coordinates, transform) {
  if (!Array.isArray(coordinates)) {
    throw new Error("Invalid GeoJSON coordinates.");
  }

  const isPosition =
    coordinates.length >= 2 &&
    typeof coordinates[0] === "number" &&
    typeof coordinates[1] === "number";

  if (isPosition) {
    return transform(coordinates);
  }

  return coordinates.map((childCoordinates) =>
    transformCoordinates(childCoordinates, transform),
  );
}

function transformAreaFeature(feature, transform) {
  return {
    ...feature,

    properties: {
      ...(feature.properties || {}),
    },

    geometry: {
      ...feature.geometry,

      coordinates: transformCoordinates(
        feature.geometry.coordinates,
        transform,
      ),
    },
  };
}

function createUtmConverter(boundary) {
  const [minLongitude, minLatitude, maxLongitude, maxLatitude] = bbox(boundary);

  const centerLongitude = (minLongitude + maxLongitude) / 2;

  const centerLatitude = (minLatitude + maxLatitude) / 2;

  const utmZone = Math.max(
    1,
    Math.min(60, Math.floor((centerLongitude + 180) / 6) + 1),
  );

  const southParameter = centerLatitude < 0 ? "+south " : "";

  const utmDefinition =
    `+proj=utm +zone=${utmZone} ` +
    southParameter +
    "+datum=WGS84 +units=m " +
    "+no_defs +type=crs";

  return proj4("EPSG:4326", utmDefinition);
}

function appendRingToMapLine(ring, longitudes, latitudes) {
  ring.forEach((position) => {
    const longitude = Number(position[0]);

    const latitude = Number(position[1]);

    if (Number.isFinite(longitude) && Number.isFinite(latitude)) {
      longitudes.push(longitude);
      latitudes.push(latitude);
    }
  });

  /*
   * null separates different polygon rings.
   */
  longitudes.push(null);
  latitudes.push(null);
}

function getBoundaryMapLines(geoJson) {
  const longitudes = [];
  const latitudes = [];

  extractAreaFeatures(geoJson).forEach((feature) => {
    if (feature.geometry.type === "Polygon") {
      feature.geometry.coordinates.forEach((ring) => {
        appendRingToMapLine(ring, longitudes, latitudes);
      });

      return;
    }

    feature.geometry.coordinates.forEach((polygonCoordinates) => {
      polygonCoordinates.forEach((ring) => {
        appendRingToMapLine(ring, longitudes, latitudes);
      });
    });
  });

  return {
    longitudes,
    latitudes,
  };
}

/**
 * Supports Turf 6 and Turf 7 intersection signatures.
 */
function intersectFeatures(firstFeature, secondFeature) {
  try {
    return intersect(featureCollection([firstFeature, secondFeature]));
  } catch (newSignatureError) {
    try {
      return intersect(firstFeature, secondFeature);
    } catch (oldSignatureError) {
      console.warn("Could not intersect GeoJSON features:", {
        newSignatureError,
        oldSignatureError,
      });

      return null;
    }
  }
}

/* =========================================================
   Standard Plotly charts
========================================================= */

function normalizeTrace(trace) {
  if (!trace || typeof trace !== "object") {
    throw new Error("Invalid Plotly trace.");
  }

  if (!SUPPORTED_TRACE_TYPES.has(trace.type)) {
    throw new Error(`Unsupported Plotly trace type: ${trace.type}`);
  }

  const sanitizedTrace = sanitizePlotlyValue(trace);

  /*
   * Plotly renders line charts as scatter traces.
   */
  if (sanitizedTrace.type === "line") {
    return {
      ...sanitizedTrace,

      type: "scatter",

      mode: sanitizedTrace.mode || "lines",
    };
  }

  return sanitizedTrace;
}

function buildStandardFigure(chart) {
  /*
   * Complete Plotly figure format:
   *
   * figure: {
   *   data: [],
   *   layout: {}
   * }
   */
  if (chart.figure && Array.isArray(chart.figure.data)) {
    const sanitizedData = chart.figure.data.map((trace) => {
      const sanitizedTrace = sanitizePlotlyValue(trace);

      if (sanitizedTrace.type === "line") {
        return {
          ...sanitizedTrace,

          type: "scatter",

          mode: sanitizedTrace.mode || "lines",
        };
      }

      return sanitizedTrace;
    });

    const sanitizedLayout = sanitizePlotlyValue(chart.figure.layout || {});

    const rtlFigure = applyRtlTitleAndLegends(sanitizedData, sanitizedLayout);

    return {
      data: rtlFigure.data,

      layout: rtlFigure.layout,

      config: buildDownloadOnlyPlotlyConfig(
        chart.figure.config || {},
        chart.chart_id || "plot",
      ),

      isMap: false,
    };
  }

  /*
   * Backend traces + layout format.
   */
  const rtlFigure = applyRtlTitleAndLegends(
    (chart.traces || []).map(normalizeTrace),
    sanitizePlotlyValue(chart.layout || {}),
  );

  return {
    data: rtlFigure.data,

    layout: rtlFigure.layout,

    config: buildDownloadOnlyPlotlyConfig({}, chart.chart_id || "plot"),

    isMap: false,
  };
}

/* =========================================================
   Province map
========================================================= */

function parseTags(properties) {
  const tags = properties?.tags;

  if (tags && typeof tags === "object") {
    return tags;
  }

  if (typeof tags === "string") {
    try {
      const parsedTags = JSON.parse(tags);

      if (parsedTags && typeof parsedTags === "object") {
        return parsedTags;
      }
    } catch {
      return {};
    }
  }

  return {};
}

function extractProvinceName(feature, locationField) {
  const properties = feature.properties || {};

  const tags = parseTags(properties);

  const nameCandidates = [
    properties[locationField],
    properties.province_name,
    properties["name:fa"],
    properties.name_fa,
    properties.name,

    tags[locationField],
    tags["name:fa"],
    tags.name,
  ];

  const matchedName = nameCandidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim().length > 0,
  );

  return matchedName || "";
}

function createProvinceLookup(provinces, locationField, valueField) {
  const exactLookup = new Map();

  const normalizedLookup = new Map();

  provinces.forEach((province, index) => {
    const provinceName = province?.[locationField] ?? province?.province_name;

    if (typeof provinceName !== "string" || provinceName.length === 0) {
      console.warn(
        `Province at index ${index} does not contain "${locationField}".`,
        province,
      );

      return;
    }

    const provinceValue = toNullableNumber(
      province.value ?? province[valueField],
    );

    exactLookup.set(provinceName, {
      value: provinceValue,

      sourceName: provinceName,
    });

    normalizedLookup.set(normalizeProvinceName(provinceName), {
      value: provinceValue,

      sourceName: provinceName,
    });
  });

  return {
    exactLookup,
    normalizedLookup,
  };
}

function findProvinceValue(provinceName, provinceLookups) {
  if (provinceLookups.exactLookup.has(provinceName)) {
    return {
      found: true,

      ...provinceLookups.exactLookup.get(provinceName),
    };
  }

  const normalizedName = normalizeProvinceName(provinceName);

  if (provinceLookups.normalizedLookup.has(normalizedName)) {
    return {
      found: true,

      ...provinceLookups.normalizedLookup.get(normalizedName),
    };
  }

  return {
    found: false,
    value: null,
    sourceName: null,
  };
}

function buildProvinceMapFigure(chartId, spec, boundary) {
  const boundaryFeatures = extractAreaFeatures(boundary);

  const provinces = Array.isArray(spec.provinces) ? spec.provinces : [];

  if (provinces.length === 0) {
    throw new Error("Province map does not contain province data.");
  }

  const locationField = spec.encoding?.location_field || "province_name";

  const valueField = spec.encoding?.value_field;

  if (!valueField) {
    throw new Error("Province map encoding.value_field is missing.");
  }

  const showComplete = spec.layers?.show_complete !== false;

  const opacity = spec.layers?.opacity ?? 0.3;

  const provinceLookups = createProvinceLookup(
    provinces,
    locationField,
    valueField,
  );

  const matchedInputNames = new Set();

  const geoJsonProvinceNames = [];
  const mapFeatures = [];

  boundaryFeatures.forEach((feature, featureIndex) => {
    const provinceName = extractProvinceName(feature, locationField);

    if (!provinceName) {
      console.warn(
        "Could not find the province name in this GeoJSON feature:",
        feature.properties,
      );

      return;
    }

    geoJsonProvinceNames.push(provinceName);

    const provinceMatch = findProvinceValue(provinceName, provinceLookups);

    if (provinceMatch.found && provinceMatch.sourceName) {
      matchedInputNames.add(provinceMatch.sourceName);
    }

    if (
      !showComplete &&
      (!provinceMatch.found || provinceMatch.value === null)
    ) {
      return;
    }

    const featureId = `province-${featureIndex}`;

    mapFeatures.push({
      ...feature,

      /*
       * Plotly locations match this top-level id.
       */
      id: featureId,

      properties: {
        ...(feature.properties || {}),

        __plotlyId: featureId,

        __provinceName: provinceName,

        __provinceValue: provinceMatch.value,
      },
    });
  });

  if (mapFeatures.length === 0) {
    throw new Error(
      "No province features could be prepared from iran_provinces_geo.json.",
    );
  }

  const locations = mapFeatures.map((feature) => String(feature.id));

  const mapValues = mapFeatures.map(
    (feature) => feature.properties.__provinceValue,
  );

  const validMapValues = mapValues.filter(
    (value) => typeof value === "number" && Number.isFinite(value),
  );

  if (validMapValues.length === 0) {
    const inputProvinceNames = provinces
      .map((province) => province?.[locationField] ?? province?.province_name)
      .filter(Boolean);

    throw new Error(
      "No province values matched the GeoJSON features. " +
        `Input names: ${inputProvinceNames.join(", ")}. ` +
        `GeoJSON names: ${geoJsonProvinceNames.join(", ")}.`,
    );
  }

  const unmatchedInputNames = provinces
    .map((province) => province?.[locationField] ?? province?.province_name)
    .filter(
      (provinceName) => provinceName && !matchedInputNames.has(provinceName),
    );

  if (unmatchedInputNames.length > 0) {
    console.warn(
      "These province names did not match GeoJSON features:",
      unmatchedInputNames,
    );
  }

  const { minimum, maximum } = resolveMapColorRange(
    validMapValues,
    spec.encoding?.range_color,
  );

  const mapGeoJson = featureCollection(mapFeatures);

  const [minLongitude, minLatitude, maxLongitude, maxLatitude] =
    bbox(mapGeoJson);

  const iranMapCenter = {
    lon: (minLongitude + maxLongitude) / 2,

    lat: (minLatitude + maxLatitude) / 2,
  };

  const iranMapZoom = Math.max(Number(spec.map?.zoom) || 0, 4.25);

  const safeValueField = escapeHtml(valueField);

  return {
    data: [
      {
        type: "choroplethmap",

        subplot: "map",

        geojson: mapGeoJson,

        featureidkey: "id",

        locations,

        z: mapValues,

        customdata: mapFeatures.map((feature) => [
          feature.properties.__provinceName,

          feature.properties.__provinceValue,
        ]),

        hovertemplate:
          "<span dir='rtl'>" +
          "<b>%{customdata[0]}</b><br>" +
          `${safeValueField}: %{customdata[1]:.2f}` +
          "</span>" +
          "<extra></extra>",

        colorscale: resolveColorScale(spec.encoding?.color_scale),

        autocolorscale: false,

        zauto: false,

        zmin: minimum,

        zmax: maximum,

        showscale: true,

        colorbar: {
          title: {
            text: valueField,
            side: "top",
          },

          thickness: 14,

          len: 0.62,

          x: 0.99,
          xanchor: "right",

          y: 0.5,
          yanchor: "middle",

          xpad: 4,
          ypad: 4,

          bgcolor: "rgba(255,255,255,0.75)",
        },

        marker: {
          opacity,

          line: {
            color: "#ffffff",
            width: 1.2,
          },
        },

        below: "",

        showlegend: false,
      },
    ],

    layout: {
      title: {
        text: makeRtlText(spec.title),

        x: 0.5,
        xanchor: "center",
      },

      autosize: true,

      uirevision: chartId,

      margin: {
        r: 10,
        t: 65,
        l: 10,
        b: 10,
      },

      map: {
        style: spec.map?.style || "carto-positron",

        center: iranMapCenter,

        zoom: iranMapZoom,
      },

      font: {
        family: "Vazirmatn, sans-serif",
      },
    },

    config: buildDownloadOnlyPlotlyConfig(
      {
        scrollZoom: true,
      },
      chartId,
    ),

    isMap: true,
  };
}

/* =========================================================
   Station map
========================================================= */

function normalizeStations(spec) {
  const stationNameField = spec.encoding?.station_name_field;

  const valueField = spec.encoding?.value_field;

  if (!stationNameField) {
    throw new Error("Station map encoding.station_name_field is missing.");
  }

  if (!valueField) {
    throw new Error("Station map encoding.value_field is missing.");
  }

  if (!Array.isArray(spec.stations) || spec.stations.length === 0) {
    throw new Error("Station map does not contain stations.");
  }

  return spec.stations.map((station, stationIndex) => {
    const longitude = station.longitude ?? station.station_longitude;

    const latitude = station.latitude ?? station.station_latitude;

    const value = station.value ?? station[valueField];

    const stationId = station.id ?? stationIndex;

    const stationName =
      station[stationNameField] ?? station.station_name ?? stationId;

    return {
      id: String(stationId),

      name: String(stationName),

      longitude: toFiniteNumber(
        longitude,
        `stations[${stationIndex}].longitude`,
      ),

      latitude: toFiniteNumber(latitude, `stations[${stationIndex}].latitude`),

      value: toNullableNumber(value),
    };
  });
}

function assertUniqueCoordinates(stations) {
  const seenCoordinates = new Set();

  stations.forEach((station) => {
    const coordinateKey =
      `${station.longitude.toFixed(10)},` + station.latitude.toFixed(10);

    if (seenCoordinates.has(coordinateKey)) {
      throw new Error(
        `Two stations have identical coordinates at ${coordinateKey}.`,
      );
    }

    seenCoordinates.add(coordinateKey);
  });
}

function findNearestProjectedStation(position, projectedStations) {
  let nearestStation = null;

  let nearestDistance = Number.POSITIVE_INFINITY;

  projectedStations.forEach((station) => {
    const deltaX = position[0] - station.x;

    const deltaY = position[1] - station.y;

    const squaredDistance = deltaX * deltaX + deltaY * deltaY;

    if (squaredDistance < nearestDistance) {
      nearestDistance = squaredDistance;

      nearestStation = station;
    }
  });

  return nearestStation;
}

function buildSingleStationVoronoi(station, boundaryFeatures) {
  return featureCollection(
    boundaryFeatures.map((feature, featureIndex) => {
      const featureId = `station-${station.id}-${featureIndex}`;

      return {
        ...feature,

        id: featureId,

        properties: {
          ...(feature.properties || {}),

          __plotlyId: featureId,

          __stationId: station.id,

          __stationName: station.name,

          __stationValue: station.value,
        },
      };
    }),
  );
}

function buildStationVoronoi(stations, boundary) {
  const boundaryFeatures = extractAreaFeatures(boundary);

  if (stations.length === 1) {
    return buildSingleStationVoronoi(stations[0], boundaryFeatures);
  }

  assertUniqueCoordinates(stations);

  const converter = createUtmConverter(boundary);

  const projectPosition = (position) => {
    const [x, y] = converter.forward([position[0], position[1]]);

    return [x, y, ...position.slice(2)];
  };

  const unprojectPosition = (position) => {
    const [longitude, latitude] = converter.inverse([position[0], position[1]]);

    return [longitude, latitude, ...position.slice(2)];
  };

  const projectedBoundaryFeatures = boundaryFeatures.map((feature) =>
    transformAreaFeature(feature, projectPosition),
  );

  const projectedBoundary = featureCollection(projectedBoundaryFeatures);

  const projectedStations = stations.map((station) => {
    const [x, y] = converter.forward([station.longitude, station.latitude]);

    return {
      ...station,
      x,
      y,
    };
  });

  const projectedStationPoints = featureCollection(
    projectedStations.map((station) =>
      point([station.x, station.y], {
        stationId: station.id,
      }),
    ),
  );

  const voronoiCells = voronoi(projectedStationPoints, {
    bbox: bbox(projectedBoundary),
  });

  if (
    !voronoiCells ||
    !Array.isArray(voronoiCells.features) ||
    voronoiCells.features.length === 0
  ) {
    throw new Error("Voronoi generation returned no polygons.");
  }

  const clippedFeatures = [];

  let generatedFeatureIndex = 0;

  voronoiCells.features.forEach((voronoiCell) => {
    if (voronoiCell?.geometry?.type !== "Polygon") {
      return;
    }

    const cellCenter = centroid(voronoiCell).geometry.coordinates;

    const station = findNearestProjectedStation(cellCenter, projectedStations);

    if (!station) {
      return;
    }

    projectedBoundaryFeatures.forEach((boundaryFeature) => {
      const clippedFeature = intersectFeatures(voronoiCell, boundaryFeature);

      if (!clippedFeature) {
        return;
      }

      const wgs84Feature = transformAreaFeature(
        clippedFeature,
        unprojectPosition,
      );

      const featureId = `station-${station.id}-${generatedFeatureIndex}`;

      clippedFeatures.push({
        ...wgs84Feature,

        id: featureId,

        properties: {
          ...(wgs84Feature.properties || {}),

          __plotlyId: featureId,

          __stationId: station.id,

          __stationName: station.name,

          __stationValue: station.value,
        },
      });

      generatedFeatureIndex += 1;
    });
  });

  if (clippedFeatures.length === 0) {
    throw new Error(
      "Voronoi polygons could not be clipped to the selected city/province boundary.",
    );
  }

  return featureCollection(clippedFeatures);
}

function buildStationMapFigure(chartId, spec, boundary) {
  const stations = normalizeStations(spec);

  const showVoronoi = spec.layers?.show_voronoi === true;

  const showScatter = spec.layers?.show_scatter === true;

  if (!showVoronoi && !showScatter) {
    throw new Error("Nothing to draw. Both station map layers are disabled.");
  }

  const valueField = spec.encoding.value_field;

  const stationValues = stations
    .map((station) => station.value)
    .filter((value) => typeof value === "number" && Number.isFinite(value));

  if (stationValues.length === 0) {
    throw new Error("Station map does not contain valid numeric values.");
  }

  const { minimum, maximum } = resolveMapColorRange(
    stationValues,
    spec.encoding?.range_color,
  );

  const colorScale = resolveColorScale(spec.encoding.color_scale);

  const safeValueField = escapeHtml(valueField);

  const hoverTemplate =
    "<span dir='rtl'>" +
    "<b>%{customdata[0]}</b><br>" +
    `${safeValueField}: %{customdata[1]:.2f}` +
    "</span>" +
    "<extra></extra>";

  const traces = [];

  if (showVoronoi) {
    const stationGeoJson = buildStationVoronoi(stations, boundary);

    traces.push({
      type: "choroplethmap",

      subplot: "map",

      geojson: stationGeoJson,

      featureidkey: "id",

      locations: stationGeoJson.features.map((feature) => String(feature.id)),

      z: stationGeoJson.features.map(
        (feature) => feature.properties.__stationValue,
      ),

      customdata: stationGeoJson.features.map((feature) => [
        feature.properties.__stationName,

        feature.properties.__stationValue,
      ]),

      hovertemplate: hoverTemplate,

      colorscale: colorScale,

      autocolorscale: false,

      zauto: false,

      zmin: minimum,

      zmax: maximum,

      showscale: true,

      colorbar: {
        title: {
          text: valueField,
        },

        thickness: 18,

        len: 0.75,
      },

      marker: {
        opacity: spec.layers?.opacity ?? 0.3,

        line: {
          color: "#ffffff",
          width: 1,
        },
      },

      below: "",

      showlegend: false,
    });
  }

  /*
   * Explicitly draw the selected city/province boundary.
   */
  const boundaryLines = getBoundaryMapLines(boundary);

  traces.push({
    type: "scattermap",

    subplot: "map",

    mode: "lines",

    lon: boundaryLines.longitudes,

    lat: boundaryLines.latitudes,

    line: {
      // color: '#014a3f',
      color: "#989898",
      width: 0.5,
    },

    hoverinfo: "skip",

    showlegend: false,
  });

  if (showScatter) {
    traces.push({
      type: "scattermap",

      subplot: "map",

      mode: "markers",

      lat: stations.map((station) => station.latitude),

      lon: stations.map((station) => station.longitude),

      customdata: stations.map((station) => [station.name, station.value]),

      hovertemplate: hoverTemplate,

      marker: {
        size: spec.layers?.scatter_size ?? 8,

        color: stations.map((station) => station.value),

        colorscale: colorScale,

        cauto: false,

        cmin: minimum,

        cmax: maximum,

        showscale: !showVoronoi,

        colorbar: !showVoronoi
          ? {
              title: {
                text: valueField,
              },

              thickness: 18,

              len: 0.75,
            }
          : undefined,

        opacity: 1,
      },

      showlegend: false,
    });
  }

  return {
    data: traces,

    layout: {
      title: {
        text: makeRtlText(spec.title),
        x: 0.5,
        xanchor: "center",
      },

      autosize: true,

      uirevision: chartId,

      margin: {
        r: 40,
        t: 60,
        l: 10,
        b: 10,
      },

      map: {
        style: spec.map?.style || "carto-positron",

        center: spec.map?.center,

        zoom: spec.map?.zoom,

        ...(spec.map?.bounds
          ? {
              bounds: spec.map.bounds,
            }
          : {}),
      },

      font: {
        family: "Vazirmatn, sans-serif",
      },
    },

    config: buildDownloadOnlyPlotlyConfig(
      {
        scrollZoom: true,
      },
      chartId,
    ),

    isMap: true,
  };
}

/* =========================================================
   Main figure resolver
========================================================= */

function isProvinceMapFigure(figure) {
  return figure && typeof figure === "object" && figure.type === "province_map";
}

function isStationMapFigure(figure) {
  return figure && typeof figure === "object" && figure.type === "station_map";
}

export async function resolveChartFigure(chart) {
  if (!chart || typeof chart !== "object") {
    throw new Error("Invalid chart object.");
  }

  const chartId = String(chart.chart_id || "unnamed-chart");

  if (isProvinceMapFigure(chart.figure)) {
    const boundaryFile = chart.figure.map?.boundary_file;

    const boundary = getGeoJsonByBoundaryFile(boundaryFile);

    return buildProvinceMapFigure(chartId, chart.figure, boundary);
  }

  if (isStationMapFigure(chart.figure)) {
    const boundaryFile = chart.figure.map?.boundary_file;

    const boundary = getGeoJsonByBoundaryFile(boundaryFile);

    return buildStationMapFigure(chartId, chart.figure, boundary);
  }

  return buildStandardFigure(chart);
}
